import React, {useState, useEffect, useRef} from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Button } from 'primereact/button';
import { useSelector, useDispatch } from 'react-redux';

import { getReseStoreTableDataInit, getReseStoreTableDivisionInit, getReseStoreTableBannerInit,
getReseStoreTableCityInit, getReseStoreTableStateInit, getReseStoreTableStatusInit, updateReseStoreTableDataInit } from './redux/actions';
import ReseStoreInfoDialouge from './ReseStoreInfoDialouge';
import { Toast } from 'primereact/toast';
import './ReseStore.scss';

const ReseStore = () => {
    const [tableFilters, setTableFilters] = useState(null);
    const [reseStoreInfoDialouge, setReseStoreInfoDialouge] = useState(null);
    const [filterCondition, setFilterCondition] = useState("")
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        sortField: "storeId",
        sortOrder: 1
    });
    const reseStoreTableRef = useRef(null);

    const reseStoreTableData = useSelector((state) => state.reseStore.reseStoreTableData);
    const totalRecords = useSelector((state) => state.reseStore.totalRecords);
    const reseStoreTableDivisions = useSelector((state) => state.reseStore.reseStoreTableDivisionData)
    const reseStoreTableBanners = useSelector((state) => state.reseStore.reseStoreTableBannerData)
    const reseStoreTableCities = useSelector((state) => state.reseStore.reseStoreTableCityData)
    const reseStoreTableStates = useSelector((state) => state.reseStore.reseStoreTableStateData)
    const reseStoreTableStatus = useSelector((state) => state.reseStore.reseStoreTableStatusData)
    const dispatch = useDispatch();
    const toast = useRef(null);
    const updateSuccess = useSelector((state) => state.reseStore.updateSuccess);
    const updateError = useSelector((state) => state.reseStore.updateError);

    useEffect(() => {
        fetchReseStoreTableData();
        initializeTableFilters();
    },[]);

    useEffect(() => {
        fetchReseStoreTableDivisions()
        fetchReseStoreTableBanners()
        fetchReseStoreTableCities()
        fetchReseStoreTableStates()
        fetchReseStoreTableStatus()
    },[]);

    useEffect(() => {
        if (updateSuccess) {
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Store Data updated successfully', life: 3000 });
            fetchReseStoreTableData();
        } else if (updateError) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to update Store Data', life: 3000 });
        }
    }, [updateSuccess, updateError]); 

    const fetchReseStoreTableData = (params=lazyParams, filters=filterCondition) => {
        const sortDirection= params.sortOrder== -1?'DESC' : 'ASC';
        const filterConditionPayload = constructFilterParams(filters);
        dispatch(getReseStoreTableDataInit(
            params.page,
            params.rows,
            params.sortField,
            sortDirection,
            filterConditionPayload
        ));
    }

    const constructFilterParams = (filterConditions) => {
        if (!filterConditions || typeof filterConditions !== "object") {
          return ""; 
        }
        const queryParams = [];
        Object.keys(filterConditions).forEach((key) => {
          const filter = filterConditions[key];
          const constraints = filter?.constraints;
          if (constraints && constraints.length > 0) {
            const { value, matchMode } = constraints[0];
            if (value !== undefined && value !== null && value !== "") {
              queryParams.push(
                `${encodeURIComponent(key)}&${encodeURIComponent(matchMode)}&${encodeURIComponent(value)}`
              );
            }
          }
        });
        return queryParams.join(",");
      };


    const fetchReseStoreTableDivisions = () => {
        const divisionColumnPayload = {
            targetColumn: "division",
            tableName: "store"
        }
        dispatch(getReseStoreTableDivisionInit(divisionColumnPayload));
    }

    const fetchReseStoreTableBanners = () => {
        const bannerColumnPayload = {
            targetColumn: "banner",
            tableName: "store"
        }
        dispatch(getReseStoreTableBannerInit(bannerColumnPayload));
    }

    const fetchReseStoreTableCities = () => {
        const cityColumnPayload = {
            targetColumn: "city",
            tableName: "store"
        }
        dispatch(getReseStoreTableCityInit(cityColumnPayload));
    }

    const fetchReseStoreTableStates = () => {
        const stateColumnPayload = {
            targetColumn: "state",
            tableName: "store"
        }
        dispatch(getReseStoreTableStateInit(stateColumnPayload));
    }

    const fetchReseStoreTableStatus = () => {
        const statusColumnPayload = {
            targetColumn: "status",
            tableName: "onboard_status"
        }
        dispatch(getReseStoreTableStatusInit(statusColumnPayload));
    }

    const initializeTableFilters = () => {
        setTableFilters({
                    global: {value: null, matchMode: FilterMatchMode.CONTAINS},
                    storeId: {
                        operator: FilterOperator.AND,
                        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
                      },
                      division: {
                        operator: FilterOperator.AND,
                        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
                      },
                      banner: {
                        operator: FilterOperator.AND,
                        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
                      },
                      city: {
                        operator: FilterOperator.AND,
                        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
                      },
                      state: {
                        operator: FilterOperator.AND,
                        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
                      },
                      status: {
                        operator: FilterOperator.AND,
                        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
                      }
                });
    };

    const onPage = (event) => {
        const onPageChangeParams = {
            ...lazyParams,
            first:event.first,
            rows:event.rows,
            page:event.page
        }
        setLazyParams(onPageChangeParams);
        fetchReseStoreTableData(onPageChangeParams);
    }

    const onSort = (event) => {
        const onSortParams = {
            ...lazyParams,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        }
        setLazyParams(onSortParams);
        fetchReseStoreTableData(onSortParams);
    }

    const onFilter = (filters) => {
        setFilterCondition(filters);
        const onFilterParams = {
            ...lazyParams,
            first: 0,
            page: 0
        }
        setLazyParams(onFilterParams);
        fetchReseStoreTableData(onFilterParams,filters);
    }


    const infoBodyTemplate = (rowData) => {
        return <i className="pi pi-info-circle" onClick={() => setReseStoreInfoDialouge(rowData)}/>
    }

    const textEditor = (options) => {
        return <InputText value={options.value} onChange={(e) => options.editorCallback(e.target.value)} />;
    }

    const divisionEditor = (options) => {
        return <Dropdown value={options.value} options={reseStoreTableDivisions} onChange={(e) => options.editorCallback(e.value)} />;
    }

    const bannerEditor = (options) => {
        return <Dropdown value={options.value} options={reseStoreTableBanners} onChange={(e) => options.editorCallback(e.value)} />;
    }

    const cityEditor = (options) => {
        return <Dropdown value={options.value} options={reseStoreTableCities} onChange={(e) => options.editorCallback(e.value)} />;
    }

    const stateEditor = (options) => {
        return <Dropdown value={options.value} options={reseStoreTableStates} onChange={(e) => options.editorCallback(e.value)} />;
    }

    const statusEditor = (options) => {
        return <Dropdown value={options.value} options={reseStoreTableStatus} onChange={(e) => options.editorCallback(e.value)} />;
    }

    const onRowEditComplete = (e) => {
        dispatch(updateReseStoreTableDataInit(e.newData));
    }

    return (
        <div className="rese-store-container container-fluid">
              <Toast ref={toast} position="bottom-center"/>
            <ReseStoreInfoDialouge dialougeData={reseStoreInfoDialouge} setDialougeData={setReseStoreInfoDialouge}/>
            <p className="page-label">
                RESE <i className="pi pi-angle-right"/> Store
            </p>
            <div className="d-flex justify-content-end">
                    <Button
                    name='refresh'
                    className='p-button-text'
                    icon="pi pi-refresh"
                    onClick={() => fetchReseStoreTableData()}
                    />
                    <Button
                    name='download'
                    className='p-button-text'
                    icon="pi pi-download"
                    onClick={() => reseStoreTableRef.current.exportCSV()}
                    />
            </div>
            <DataTable
            value={reseStoreTableData}
            ref={reseStoreTableRef}
            showGridlines
            lazy
            totalRecords={totalRecords}
            // header={tableHeader}
            filters={tableFilters}
            filterDisplay='menu'
            onFilter={(e) => onFilter(e.filters)}
            // globalFilterFields={['storeId', 'division', 'banner', 'city', 'state', 'status']}
            sortField={lazyParams.sortField}
            sortOrder={lazyParams.sortOrder}
            onSort={onSort}
            first={lazyParams.first}
            rows={lazyParams.rows}
            rowsPerPageOptions={[5, 10, 20]}
            onPage={onPage}
            editMode='row'
            onRowEditComplete={onRowEditComplete}
            paginator
            paginatorTemplate="CurrentPageReport RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
           currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
            >
                <Column field="storeId" header="Store ID" sortable filter/>
                <Column field="division" header="Division" editor={(options) => divisionEditor(options)} sortable filter/>
                <Column field="banner" header="Banner" editor={(options) => bannerEditor(options)} sortable filter/>
                <Column field="streetAddress" header="Street Adress" editor={(options) => textEditor(options)} sortable />
                <Column field="city" header="City" editor={(options) => cityEditor(options)} sortable filter/>
                <Column field="state" header="State" editor={(options) => stateEditor(options)} sortable filter/>
                <Column field="zip" header="Zip" sortable />
                <Column field="sqft" header="Sqft"  sortable />
                <Column field="phone" header="Phone" editor={(options) => textEditor(options)} sortable />
                <Column field="latitude" header="Latitude" sortable />
                <Column field="longitude" header="Longitude" sortable />
                <Column field="status" header="Status" editor={(options) => statusEditor(options)} sortable filter/>
                <Column header="Info" body={infoBodyTemplate}/>
                <Column rowEditor bodyStyle={{textAlign: 'center', minWidth: '7rem'}}/>

            </DataTable>

        </div>
    );
}

export default ReseStore;